import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LessonCom from "./LessonCom";
import { GLOBAL_IMAGES, YearBg } from "../../assets/asset";
import { LessonSkeleton, SkeletonHeader } from "../../utils/SkeletonLoader";
import { UseYearColor } from "../../hooks/useYearColor";
import { useDispatch, useSelector } from "react-redux";
import { setScrollPosition } from "../../store/slice/scrollPositionSlice";
import axiosInstance from "../../api/axiosInstance";
import { listLessonsV2 } from "../../services/lessonV2.service";
import Knowledge from "./Knowledge";
import { getLogicalSize } from "../../hooks/useShellViewport";
import { setShowKnowledge } from "../../store/slice/profileSlice";
import { setOlderValueWas } from "../../store/slice/muteSlice";
import backArrowImage from "../../assets/global/Back.png";
import YearCompletionPopup from "../ui/YearCompletionPopup";
import { useUpdateUser } from "../../features/auth/useLogin";
import { useYears } from "../../features/queryHooks/useYears";
import { useSubscription } from "../../context/SubscriptionContext";
import {
  getAccessibleBasicYearId,
  getCanonicalYearId,
  getCurrentUnlockedYearId,
  formatYearLabel,
  getNextYearMeta,
  getUserId,
  isPaidBasicUser,
} from "../../utils/basicPlanAccess";
import { toast } from "react-toastify";
import { saveContinueLearning, clearContinueLearning } from "../../utils/continueLearning";
import { isLessonCompleted, markLessonCompleted } from "../../utils/lessonProgress";

// Loading Screen Component
const LoadingScreen = () => (
  <div className="app-loading-screen app-loading-screen-dark fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900">
    <div className="app-loading-card text-center">
      <div className="relative mb-8">
        <div className="app-loading-spinner animate-spin rounded-full h-20 w-20 border-b-4 border-white"></div>
        <div className="absolute inset-0 rounded-full border-2 border-white opacity-30"></div>
      </div>
      <h2 className="app-loading-title text-3xl font-bold text-white mb-3 font-mali">
        Loading Lessons
      </h2>
      <p className="app-loading-copy text-blue-200 text-lg font-mali">
        Preparing your learning experience...
      </p>
      <div className="app-loading-dots mt-6 flex justify-center space-x-2">
        <div className="w-3 h-3 bg-white rounded-full animate-bounce"></div>
        <div
          className="w-3 h-3 bg-white rounded-full animate-bounce"
          style={{ animationDelay: "0.1s" }}
        ></div>
        <div
          className="w-3 h-3 bg-white rounded-full animate-bounce"
          style={{ animationDelay: "0.2s" }}
        ></div>
      </div>
    </div>
  </div>
);

const LessonDashboard = () => {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const { showKnowledge, currentYear } = useSelector((state) => state.profile);
  const { normalizedPlanType } = useSubscription();
  const [selectedIcon, setSelectedIcon] = useState("notebook");
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const { scrollPosition, activeYear } = useSelector(
    (state) => state.scrollPosition,
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [lastLessonPKValue, setLastLessionPKValue] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { yearId: stateYearId, yearCompletionStatus, completedLessonId } = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const persistedYearId = getCanonicalYearId(currentYear);
  const rawYearId =
    stateYearId ?? searchParams.get("yearId") ?? persistedYearId ?? null;
  const yearId = Number(rawYearId);
  const isYearIdValid = Number.isFinite(yearId) && yearId > 0;
  const safeYearCompletionStatus = +yearCompletionStatus;
  const isBasicPlanUser =
    normalizedPlanType === "basic" || isPaidBasicUser(user);
  const currentUnlockedYearId = isBasicPlanUser
    ? getAccessibleBasicYearId(user)
    : getCurrentUnlockedYearId(user);

  const scrollContainerRef = useRef(null);
  const yearAdvanceRef = useRef("");
  const colorTheme = UseYearColor(isYearIdValid ? yearId - 1 : 0);
  const updateUserMutation = useUpdateUser();
  const yearsQuery = useYears();

  const scrollAmount = window.innerWidth * 0.8;

  const updateScrollControls = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    );
  };

  // Image preloading functions
  const preloadImage = (src) => {
    return new Promise((resolve, reject) => {
      if (!src) {
        resolve(); // Skip loading if src is null/undefined
        return;
      }

      const img = new Image();
      img.onload = resolve;
      img.onerror = () => {
        console.warn(`Failed to load image: ${src}`);
        resolve(); // Resolve instead of reject to not block the page
      };
      img.src = src;
    });
  };

  const preloadAllImages = async () => {
    const imagesToPreload = [
      isYearIdValid ? YearBg[yearId - 1] : null, // Background image for current year
      GLOBAL_IMAGES.dingo, // Logo
      GLOBAL_IMAGES.notebook, // Notebook icon
      GLOBAL_IMAGES.knowledgeCard, // Knowledge card icon
      GLOBAL_IMAGES.medal, // Medal icon
      user?.profilephotos, // User profile photo (optional)
      backArrowImage, // Back arrow
      GLOBAL_IMAGES.next, // Next arrow
    ].filter(Boolean); // Remove null/undefined values

    try {
      await Promise.all(imagesToPreload.map((src) => preloadImage(src)));
      setImagesLoaded(true);
    } catch (error) {
      console.error("Error preloading images:", error);
      // Still show the page even if some images fail to load
      setImagesLoaded(true);
    }
  };

  // Preload images on component mount
  useEffect(() => {
    preloadAllImages();
  }, [yearId, user?.profilephotos, isYearIdValid]);

  useEffect(() => {
    const timerId = setTimeout(updateScrollControls, 0);
    window.addEventListener("resize", updateScrollControls);
    return () => {
      clearTimeout(timerId);
      window.removeEventListener("resize", updateScrollControls);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        dispatch(setScrollPosition(container.scrollLeft));
        updateScrollControls();
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [dispatch]);

  useEffect(() => {
    updateScrollControls();
  }, [data, loading, imagesLoaded]);

  // Scroll to the last-clicked lesson when returning from a completed lesson.
  // We read the saved lesson index from sessionStorage (set in handleLessonClick)
  // and scroll to that element once everything is rendered.
  useEffect(() => {
    if (!imagesLoaded || loading || !data?.data?.length) return;
    if (!scrollContainerRef.current) return;

    let savedIndex = -1;
    try {
      savedIndex = Number(
        sessionStorage.getItem("lastClickedLessonIndex") ?? -1,
      );
    } catch (_) {
      savedIndex = -1;
    }

    if (savedIndex < 0) return;

    const timerId = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container) return;

      const targetEl = container.querySelector(
        `[data-lesson-index="${savedIndex}"]`,
      );
      if (!targetEl) return;

      const containerRect = container.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();
      const scrollOffset =
        targetRect.left -
        containerRect.left +
        container.scrollLeft -
        containerRect.width / 3;

      container.scrollTo({
        left: Math.max(0, scrollOffset),
        behavior: "smooth",
      });

      // Clear so it doesn't re-scroll on next render
      try {
        sessionStorage.removeItem("lastClickedLessonIndex");
      } catch (_) {}
    }, 400);
    return () => clearTimeout(timerId);
  }, [data, loading, imagesLoaded]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      setTimeout(() => {
        dispatch(setScrollPosition(container.scrollLeft));
        updateScrollControls();
      }, 300);
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      container.scrollBy({ left: scrollAmount, behavior: "smooth" });
      setTimeout(() => {
        dispatch(setScrollPosition(container.scrollLeft));
        updateScrollControls();
      }, 300);
    }
  };

  const processLessons = (lessons) => {
    if (!lessons) return [];

    let regularLessonCount = 0;
    let testLessonCount = 0;

    return lessons.map((lesson, index) => {
      const isTestLesson = lesson.fields.lession.toLowerCase().includes("test");
      let displayNumber;

      if (isTestLesson) {
        testLessonCount++;
        displayNumber = `T${testLessonCount}`;
      } else {
        regularLessonCount++;
        displayNumber = regularLessonCount;
      }

      return {
        ...lesson,
        isTestLesson,
        displayNumber,
        displayIndex: index,
      };
    });
  };

  useEffect(() => {
    if (!isYearIdValid) {
      setLoading(false);
      navigate("/yearsdashboard", { replace: true });
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch legacy lessons and V2 lessons in parallel
        const [legacyResult, v2Result] = await Promise.all([
          axiosInstance.post("api/lessionofyear", { YearId: yearId }),
          listLessonsV2(yearId).catch((err) => {
            console.warn("[LessonV2] Parallel fetch failed, continuing with legacy only:", err);
            return { isSuccessful: false, data: [] };
          }),
        ]);

        const legacyLessons = (legacyResult.data.data || []).map((lesson) => {
          const isCompleted =
            (lesson.fields?.attempt && String(lesson.fields.attempt) !== "0") ||
            isLessonCompleted(user, lesson.pk);
          return {
            ...lesson,
            fields: {
              ...lesson.fields,
              attempt: isCompleted
                ? lesson.fields?.attempt && String(lesson.fields.attempt) !== "0"
                  ? lesson.fields.attempt
                  : "1"
                : "0",
            },
          };
        });

        // Wrap V2 lessons into the legacy lesson shape and append them.
        // Only include genuinely new content-generator lessons (metadata.source === "content_generator"),
        // not legacy-mirrored V2 entries which are already displayed from the legacy API.
        let v2Lessons = [];
        if (v2Result?.isSuccessful && Array.isArray(v2Result.data) && v2Result.data.length > 0) {
          v2Lessons = v2Result.data
            .filter((v2) => v2?.metadata?.source === "content_generator" && v2?.yearId === yearId)
            .map((v2) => {
              const isCompleted =
                isLessonCompleted(user, `v2-${v2.id}`) ||
                isLessonCompleted(user, v2.id);
              return {
                pk: `v2-${v2.id}`,
                isV2: true,
                v2Data: v2,
                fields: {
                  lession: v2.name || "V2 Lesson",
                  lessonTopics: v2.name || "V2 Lesson",
                  attempt: isCompleted ? "1" : "0",
                },
              };
            });
        }

        const allLessons = [...legacyLessons, ...v2Lessons];

        const processedData = {
          ...legacyResult,
          data: processLessons(allLessons),
        };
        setData(processedData);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [yearId, isYearIdValid, navigate, user]);

  useEffect(() => {
    if (data) setLastLessionPKValue(data?.data[data?.data.length - 1]?.pk);
  }, [data]);

  // Handle auto-advance logic for Continue Learning when returning from a completed lesson.
  useEffect(() => {
    if (!data?.data?.length || !completedLessonId) return;

    // We only want to process this auto-advance once per completed lesson state
    const processedKey = `processedCompletedLesson_${completedLessonId}`;
    if (sessionStorage.getItem(processedKey)) return;
    
    try {
      sessionStorage.setItem(processedKey, "true");
      markLessonCompleted(user, completedLessonId);
    } catch (_) {}

    const currentIndex = data.data.findIndex((l) => {
      const matchesPk = String(l.pk) === String(completedLessonId);
      const matchesV2Id =
        l.v2Data &&
        (String(l.v2Data.id) === String(completedLessonId).replace(/^v2-/, "") ||
          String(l.pk) === String(completedLessonId));
      const matchesNumeric = Number(l.pk) === Number(completedLessonId);
      return matchesPk || matchesV2Id || matchesNumeric;
    });
    
    if (currentIndex >= 0 && currentIndex < data.data.length - 1) {
      const nextLesson = data.data[currentIndex + 1];
      saveContinueLearning(user, {
        lessonId: nextLesson.pk,
        yearId,
        colorTheme,
        lessonDisplayName: String(nextLesson.displayNumber),
        lessonTitle: String(nextLesson.fields?.lessonTopics || "").trim(),
        lessonIndex: nextLesson.displayIndex,
        phase: "video",
        questionIndex: 0,
        yearLabel: formatYearLabel(yearId),
      });
    } else {
      // If there is no next lesson, or lesson not found, clear the banner
      clearContinueLearning(user);
    }
  }, [data, completedLessonId, user, yearId, colorTheme]);

  useEffect(() => {
    if (!isYearIdValid || !data?.data?.length) return;
    if (!isBasicPlanUser) return;
    if (currentUnlockedYearId !== yearId) return;

    const allLessonsCompleted = data.data.every(
      (lesson) => String(lesson?.fields?.attempt) === "1",
    );

    if (!allLessonsCompleted) return;

    const nextYearMeta = getNextYearMeta(yearId, yearsQuery.data?.data ?? []);
    if (!nextYearMeta?.yearid) return;

    const advanceKey = `${yearId}:${nextYearMeta.yearid}`;
    if (yearAdvanceRef.current === advanceKey) return;

    yearAdvanceRef.current = advanceKey;
    updateUserMutation.mutate(
      {
        id: getUserId(user),
        yearid: nextYearMeta.yearid,
        year: nextYearMeta.year,
      },
      {
        onError: (error) => {
          toast.error(
            error?.response?.data?.message ||
              "Unable to unlock the next year right now.",
            {
              ariaLabel: "Year Unlock Error",
              autoClose: 2000,
              theme: "colored",
              hideProgressBar: true,
              className: "font-mali",
            },
          );
        },
      },
    );
  }, [
    currentUnlockedYearId,
    data,
    isBasicPlanUser,
    isYearIdValid,
    updateUserMutation,
    user,
    yearId,
    yearsQuery.data?.data,
  ]);

  const handleLessonClick = (lesson) => {
    const isV2 = Boolean(lesson.isV2);
    // For V2 lessons, use the actual numeric id; for legacy, use pk
    const lessonId = isV2 ? lesson.v2Data.id : lesson.pk;
    const v2Video = isV2 ? (lesson.v2Data?.video || "") : "";
    const lessonContext = {
      lessonId,
      yearId,
      colorTheme,
      lessonDisplayName: lesson.displayNumber,
      isV2,
      v2Video,
    };

    try {
      sessionStorage.setItem("lessonContext", JSON.stringify(lessonContext));
      // Save lesson index so we can scroll back to this position on return
      sessionStorage.setItem(
        "lastClickedLessonIndex",
        String(lesson.displayIndex),
      );
    } catch (_) {
      // Ignore storage errors.
    }

    saveContinueLearning(user, {
      lessonId,
      yearId,
      colorTheme,
      lessonDisplayName: String(lesson.displayNumber),
      lessonTitle: String(lesson.fields?.lessonTopics || "").trim(),
      lessonIndex: lesson.displayIndex,
      phase: "video",
      questionIndex: 0,
      yearLabel: formatYearLabel(yearId),
    });

    dispatch(setOlderValueWas());
    navigate(
      `/foundationLessonCom?lessonId=${lessonId}&yearId=${yearId}&lessonDisplayName=${encodeURIComponent(
        lesson.displayNumber,
      )}`,
      {
        state: { ...lessonContext, lastLesson: lastLessonPKValue },
      },
    );
  };

  const renderLessons = () => {
    if (!data?.data) return null;

    return data.data.map((lesson) => {
      const isEvenLesson = lesson.displayIndex % 2 === 0;
      const lessonClass = `flex-shrink-0 ${
        isEvenLesson ? "relative -top-4" : "relative top-4"
      }`;
      return (
        <div
          className={`lesson-dashboard-card-offset ${
            isEvenLesson
              ? "lesson-dashboard-card-up"
              : "lesson-dashboard-card-down"
          } ${lessonClass}`}
          key={lesson.pk}
          data-lesson-index={lesson.displayIndex}
        >
          <LessonCom
            lessonTopics={lesson.fields.lessonTopics}
            index={lesson.displayIndex}
            yearId={yearId}
            isAttempt={lesson.fields.attempt}
            isTestLesson={lesson.isTestLesson}
            displayNumber={lesson.displayNumber}
            onClick={() => handleLessonClick(lesson)}
          />
        </div>
      );
    });
  };

  const closeKnowledge = () => {
    dispatch(setShowKnowledge(false));
    setSelectedIcon("notebook");
  };

  // Show loading screen until images are loaded
  if (!imagesLoaded) {
    return <LoadingScreen />;
  }

  return (
    <div
      className="lesson-dashboard-screen flex w-screen min-h-screen overflow-y-auto items-center"
      style={{
        backgroundImage: `url(${isYearIdValid ? YearBg[yearId - 1] : GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        width: "100%",
        minHeight: "100vh",
      }}
    >
      {safeYearCompletionStatus === 1 && <YearCompletionPopup />}
      {showKnowledge && (
        <Knowledge closeKnowledge={closeKnowledge} year={yearId} />
      )}

      <>
        <nav
          className="lesson-dashboard-nav fixed z-10 top-0 left-0 w-full flex items-center justify-between px-3 md:px-6 py-2 md:py-4 font-mali"
          style={{ background: "rgba(0, 0, 0, 0.60)" }}
        >
          {loading ? (
            <SkeletonHeader />
          ) : (
            <>
              <div className="lesson-dashboard-nav-left flex items-center gap-2 md:gap-5">
                <Link to={`/yearsdashboard`}>
                  <img
                    src={GLOBAL_IMAGES.dingo}
                    alt="Logo"
                    className="lesson-dashboard-logo w-10 h-10 md:w-16 md:h-16 object-contain flex-shrink-0"
                  />
                </Link>
                <h1 className="lesson-dashboard-title ml-2 md:ml-4 text-white text-lg md:text-3xl font-bold">
                  Year {yearId === 1 ? "Foundation" : yearId - 1}
                </h1>
              </div>
              <div className="lesson-dashboard-nav-center absolute left-1/2 transform -translate-x-1/2 flex items-center gap-4 md:gap-10">
                <div className="flex flex-col items-center">
                  <img
                    src={GLOBAL_IMAGES.notebook}
                    className="lesson-dashboard-nav-icon w-10 h-10 md:w-16 md:h-16 object-contain flex-shrink-0 cursor-pointer"
                    onClick={() => setSelectedIcon("notebook")}
                  />
                  {selectedIcon === "notebook" && (
                    <div className="h-2 w-2 rounded-full bg-blue-500 mt-1"></div>
                  )}
                </div>

                <div
                  className="flex flex-col items-center cursor-pointer"
                  onClick={() => {
                    dispatch(setShowKnowledge(true));
                    setSelectedIcon("knowledge");
                  }}
                >
                  <img
                    src={GLOBAL_IMAGES.knowledgeCard}
                    className="lesson-dashboard-nav-icon w-10 h-10 md:w-16 md:h-16 object-contain flex-shrink-0"
                    alt="Knowledge Card"
                  />
                  {selectedIcon === "knowledge" && (
                    <div className="h-2 w-2 rounded-full bg-blue-500 mt-1"></div>
                  )}
                </div>

                <div className="flex flex-col items-center">
                  <Link
                    to="/leaderboard"
                    onClick={() => setSelectedIcon("medal")}
                  >
                    <img
                      src={GLOBAL_IMAGES.medal}
                      className="lesson-dashboard-nav-icon w-10 h-10 md:w-16 md:h-16 object-contain flex-shrink-0"
                    />
                  </Link>
                  {selectedIcon === "medal" && (
                    <div className="h-2 w-2 rounded-full bg-blue-500 mt-1"></div>
                  )}
                </div>
              </div>
              <Link to={`/settings`}>
                <img
                  src={user.profilephotos && user.profilephotos}
                  alt="profile-avatar"
                  className="lesson-dashboard-avatar w-10 h-10 md:w-16 md:h-16 rounded-full object-cover flex-shrink-0"
                />
              </Link>
            </>
          )}
        </nav>

        <div className="lesson-dashboard-arrows flex items-center w-full self-center mx-auto absolute z-10 top-[60px] md:top-[80px] mt-4">
          <div className="lesson-dashboard-arrows-inner flex items-center w-full justify-between mx-8 md:mx-32">
            {canScrollLeft && (
              <div
                onClick={scrollLeft}
                className="cursor-pointer items-center justify-center  "
              >
                <img
                  src={backArrowImage}
                  className="lesson-dashboard-arrow-img"
                  alt="Back"
                />
              </div>
            )}
            {canScrollRight && (
              <div
                onClick={scrollRight}
                className="cursor-pointer items-center justify-center relative"
                style={{ marginLeft: canScrollLeft ? "0" : "auto" }}
              >
                <img
                  src={GLOBAL_IMAGES.next}
                  className="lesson-dashboard-arrow-img z-[30] "
                  alt="Next"
                />
              </div>
            )}
          </div>
        </div>

        <div
          ref={scrollContainerRef}
          className="lesson-dashboard-scroll flex items-center gap-8 md:gap-16 overflow-x-auto w-full px-8 md:px-20 mt-12 scroll-smooth"
          style={{
            minWidth: "100%",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            WebkitOverflowScrolling: "touch",
            paddingBottom: "20px",
          }}
        >
          <div className="lesson-dashboard-track flex gap-8 md:gap-16 min-w-max px-4 md:px-10">
            {loading
              ? [...Array(5)].map((_, index) => (
                  <LessonSkeleton key={index} index={index} />
                ))
              : renderLessons()}
          </div>
        </div>
      </>
    </div>
  );
};

export default LessonDashboard;
